import { useEffect, useState, createContext, useContext } from 'react';
import { Drawer, DialogProps, ContentProps } from 'vaul';

import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { withStyles } from '../../../utils';
import styles from './BottomDrawer.module.scss';
import Title2 from '../../Text/Title/Title2';

const cn = withStyles(styles);

type SnapPoint = number | string;

export type BottomDrawerTypes = {
  SnapPoint: SnapPoint;
};

const BottomDrawerContext = createContext<{
  snap?: SnapPoint | null;
  atLastSnap: boolean;
  snapPoints?: SnapPoint[] | null;
}>({
  atLastSnap: false,
});

export default function BottomDrawer({
  children,
  ...props
}: DialogProps & {
  offset?: number;
}) {
  const { snapPoints, open } = props;
  const [snap, setSnap] = useState<SnapPoint | null>(snapPoints ? snapPoints[0] : null);

  const atLastSnap = Boolean(snapPoints && snap === snapPoints[snapPoints.length - 1]);

  useEffect(() => {
    if (snapPoints && snapPoints[0]) {
      setSnap(snapPoints[0]);
    }
  }, [snapPoints]);

  // Prevent background scrolling when drawer is open
  useEffect(() => {
    if (open) {
      // Store original values in case they were set
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [open]);

  const snapPointProps = snapPoints
    ? {
        snapPoints,
        fadeFromIndex: props.fadeFromIndex || 1,
      }
    : {};

  return (
    <BottomDrawerContext.Provider value={{ snapPoints, snap, atLastSnap }}>
      <Drawer.Root
        handleOnly={atLastSnap}
        modal={false}
        {...props}
        {...snapPointProps}
        activeSnapPoint={snap}
        setActiveSnapPoint={setSnap}
      >
        {children}
      </Drawer.Root>
    </BottomDrawerContext.Provider>
  );
}

const Trigger = Drawer.Trigger;
Trigger.displayName = 'Trigger';
BottomDrawer.Trigger = Trigger;

function Content({ children, className, ...props }: ContentProps) {
  const { snapPoints, atLastSnap } = useContext(BottomDrawerContext);

  return (
    <Drawer.Portal>
      {/* Custom overlay that doesn't block pointer events */}
      <div className={cn('overlay')} />

      <Drawer.Content
        aria-describedby={undefined}
        {...props}
        style={{
          ...props.style,
        }}
        className={cn('drawerContentContainer', className)}
      >
        <Drawer.Handle className={cn('handle')} />

        <div className={cn('drawerContent', 'scrollbar', snapPoints && !atLastSnap && 'overflowHidden')}>
          {children}
        </div>
      </Drawer.Content>
    </Drawer.Portal>
  );
}

Content.displayName = 'Content';
BottomDrawer.Content = Content;

type TitleProps = React.ComponentPropsWithoutRef<typeof Drawer.Title> & {
  hidden?: boolean;
};

function Title({ hidden = false, ...props }: TitleProps) {
  if (hidden) {
    return (
      <VisuallyHidden.Root>
        <Drawer.Title {...props} />
      </VisuallyHidden.Root>
    );
  }

  return (
    <Drawer.Title {...props} className={cn('title', props.className)} asChild>
      <Title2 level="2">{props.children}</Title2>
    </Drawer.Title>
  );
}

BottomDrawer.Title = Title;
BottomDrawer.Close = Drawer.Close;
